import { Menu as MenuPrimitive } from '@base-ui/react/menu';
import { $createLinkNode, $toggleLink, LinkNode } from '@lexical/link';
import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    ListNode,
    REMOVE_LIST_COMMAND,
    type ListType,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import {
    $createTextNode,
    $getSelection,
    $isRangeSelection,
    $setSelection,
    COMMAND_PRIORITY_LOW,
    FORMAT_TEXT_COMMAND,
    SELECTION_CHANGE_COMMAND,
    type BaseSelection,
} from 'lexical';
import {
    BoldIcon,
    EllipsisVerticalIcon,
    ItalicIcon,
    LinkIcon,
    ListIcon,
    ListOrderedIcon,
    StrikethroughIcon,
    UnderlineIcon,
} from 'lucide-react';
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
    type ComponentPropsWithRef,
    type ReactElement,
    type ReactNode,
    type RefObject,
} from 'react';
import { ToolbarIconButton } from '../candidates/toolbar-icon-button.tsx';
import { Container } from '../containers/container.tsx';
import { Menu, MenuItem, MenuItemLabel, MenuItemLeading } from '../primitives/menu.tsx';
import { Tooltip, TooltipContent, TooltipTrigger } from '../primitives/tooltip.tsx';
import { cn } from '../util/classes.ts';
import { useLatestRef } from '../util/use-latest-ref.ts';

export type RichTextFormat =
    | 'bold'
    | 'italic'
    | 'underline'
    | 'strikethrough'
    | 'ordered-list'
    | 'bullet-list';

/** Inline formats, which Lexical tracks on the selection. */
export type RichTextTextFormat = Extract<RichTextFormat, 'bold' | 'italic' | 'underline' | 'strikethrough'>;

/** Block formats. At most one is active, and applying the active one removes it. */
export type RichTextListFormat = Extract<RichTextFormat, 'ordered-list' | 'bullet-list'>;

/**
 * Everything a control can show an on-state for. Wider than `RichTextFormat` because a
 * link has no command to apply — it needs a URL first — but the selection is still
 * either in one or not.
 */
export type RichTextActive = RichTextFormat | 'link';

const textFormats: RichTextTextFormat[] = ['bold', 'italic', 'underline', 'strikethrough'];

const isListFormat = (format: RichTextFormat): format is RichTextListFormat =>
    format === 'ordered-list' || format === 'bullet-list';

// `check` is absent: the bar has no checklist control, so one reports no active format.
const listFormatByType: Partial<Record<ListType, RichTextListFormat>> = {
    number: 'ordered-list',
    bullet: 'bullet-list',
};

// One selection subscription per field, shared by every button and menu row.
const ActiveFormatsContext = createContext<ReadonlySet<RichTextActive>>(new Set());

const sameFormats = (a: ReadonlySet<RichTextActive>, b: ReadonlySet<RichTextActive>): boolean =>
    a.size === b.size && [...a].every((format) => b.has(format));

function useActiveFormats(): ReadonlySet<RichTextActive> {
    const [editor] = useLexicalComposerContext();
    const [active, setActive] = useState<ReadonlySet<RichTextActive>>(new Set());

    const $read = useCallback(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
            return;
        }

        const next = new Set<RichTextActive>(textFormats.filter((format) => selection.hasFormat(format)));
        const anchorNode = selection.anchor.getNode();
        // Lists are block state, not selection formatting, so read them from the tree.
        const list = $getNearestNodeOfType(anchorNode, ListNode);
        const listFormat = list ? listFormatByType[list.getListType()] : undefined;
        if (listFormat) {
            next.add(listFormat);
        }
        // `AutoLinkNode` extends `LinkNode`, so a matched URL reports as a link too.
        if ($getNearestNodeOfType(anchorNode, LinkNode)) {
            next.add('link');
        }

        // Runs on every keystroke, so a fresh Set each time would re-render every control.
        setActive((current) => (sameFormats(current, next) ? current : next));
    }, []);

    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read($read);
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                () => {
                    $read();
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
    }, [editor, $read]);

    return active;
}

/**
 * Provides the state `useRichTextFormatting` reads. It sits in `RichTextInput`, not in
 * `RichTextToolbar`, so a component that renders the toolbar can read it too.
 */
export function RichTextFormattingProvider({ children }: { children?: ReactNode }): ReactElement {
    const activeFormats = useActiveFormats();

    return <ActiveFormatsContext.Provider value={activeFormats}>{children}</ActiveFormatsContext.Provider>;
}

/** Drives the same commands and state as the plugins. Only valid inside a `RichTextInput`. */
export function useRichTextFormatting(): {
    active: ReadonlySet<RichTextActive>;
    apply: (format: RichTextFormat) => void;
    isActive: (format: RichTextActive) => boolean;
} {
    const [editor] = useLexicalComposerContext();
    const active = useContext(ActiveFormatsContext);

    const apply = useCallback(
        (format: RichTextFormat) => {
            if (!isListFormat(format)) {
                editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
                return;
            }
            // Applying the active list removes it — the insert commands alone are one-way.
            if (active.has(format)) {
                editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
            } else if (format === 'ordered-list') {
                editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            } else {
                editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            }
        },
        [editor, active],
    );

    const isActive = useCallback((format: RichTextActive) => active.has(format), [active]);

    return { active, apply, isActive };
}

/** What a control contributes to the ⋮ menu when it collapses. */
export type RichTextToolbarItem = {
    /** Accessible name; this package owns no copy, so callers translate. */
    label: string;
    /** A node, so the same element renders in bar and row. */
    icon: ReactNode;
    onSelect: () => void;
    /** Drives the row's on-state; omit for an action that has none. */
    format?: RichTextActive;
};

/** As stored: the handler behind a ref, so a row runs the newest one. */
type RegisteredItem = Omit<RichTextToolbarItem, 'onSelect'> & {
    onSelect: RefObject<() => void>;
};

type RichTextToolbarContextValue = {
    /** Mount order, which is bar order — except a control mounted later lands at the end. */
    items: ReadonlyMap<string, RegisteredItem>;
    setItem: (key: string, item: RegisteredItem) => void;
    removeItem: (key: string) => void;
    /** The bar itself, so the ⋮ popup aligns to the field and not to the button. */
    barRef: RefObject<HTMLDivElement | null>;
};

const RichTextToolbarContext = createContext<RichTextToolbarContextValue | undefined>(undefined);

function useRichTextToolbarContext(component: string): RichTextToolbarContextValue {
    const context = useContext(RichTextToolbarContext);
    if (!context) {
        throw new Error(`${component} must be rendered inside a <RichTextToolbar>.`);
    }
    return context;
}

/**
 * Registers a control with the ⋮ menu, so an action the bar hides stays reachable.
 *
 * Membership and content are two effects: one cleanup that also ran when `label` or
 * `icon` changed identity would drop and re-add the row every render.
 */
export function useRichTextToolbarItem(key: string, item: RichTextToolbarItem, collapse: boolean): void {
    const { setItem, removeItem } = useRichTextToolbarContext('useRichTextToolbarItem');
    const { label, icon, format } = item;
    const onSelect = useLatestRef(item.onSelect);

    useEffect(() => {
        if (!collapse) {
            return undefined;
        }
        return () => {
            removeItem(key);
        };
    }, [key, collapse, removeItem]);

    useEffect(() => {
        if (!collapse) {
            return;
        }
        setItem(key, { label, icon, format, onSelect });
    }, [key, collapse, label, icon, format, onSelect, setItem]);
}

export type RichTextToolbarProps = ComponentPropsWithRef<'div'>;

/**
 * The formatting bar under a `RichTextInput`. Compose it from the plugins below — the
 * bar and the ⋮ menu are two views of one registry, so a control joins both.
 */
export function RichTextToolbar({ className, children, ref, ...props }: RichTextToolbarProps): ReactElement {
    const barRef = useRef<HTMLDivElement | null>(null);
    const [items, setItems] = useState<ReadonlyMap<string, RegisteredItem>>(new Map());

    const setItem = useCallback((key: string, item: RegisteredItem) => {
        setItems((current) => {
            const previous = current.get(key);
            // Icon identity is skipped: an inline `<Icon />` is a new element every render.
            if (
                previous &&
                previous.label === item.label &&
                previous.format === item.format &&
                previous.onSelect === item.onSelect
            ) {
                return current;
            }
            // `set` keeps an existing key's position, so a relabelled control stays put.
            return new Map(current).set(key, item);
        });
    }, []);

    const removeItem = useCallback((key: string) => {
        setItems((current) => {
            if (!current.has(key)) {
                return current;
            }
            const next = new Map(current);
            next.delete(key);
            return next;
        });
    }, []);

    return (
        <RichTextToolbarContext.Provider value={{ items, setItem, removeItem, barRef }}>
            <Container
                data-slot="rich-text-toolbar"
                level={2}
                className={cn(
                    '@container flex flex-row items-center gap-tight',
                    'border-t border-line-weak px-cozy py-1.5',
                    // Force all buttons to be the same size on desktop
                    'sm:**:[button]:size-6 sm:**:[button]:min-w-6',
                    "sm:**:[svg:not([class*='size-'])]:size-3.5",
                    className,
                )}
                ref={(node) => {
                    barRef.current = node;
                    if (typeof ref === 'function') {
                        ref(node);
                    } else if (ref) {
                        ref.current = node;
                    }
                }}
                {...props}
            >
                {children}
            </Container>
        </RichTextToolbarContext.Provider>
    );
}

export type RichTextToolbarButtonProps = ComponentPropsWithRef<'button'> & {
    /** Accessible name; this package owns no copy, so callers translate. */
    label: string;
    /** Fold into the overflow menu on a narrow editor. */
    collapse?: boolean;
    format?: RichTextFormat;
    /** On-state for a custom action; `format` buttons derive their own. */
    selected?: boolean;
};

/**
 * The bar's button shape. Its icon is the child; `format` wires the command and the
 * pressed state.
 *
 * It renders one element and no tooltip of its own, so a `Tooltip` or a menu can take
 * it as a `render` target. Prefer the plugins below; reach for this only for a control
 * they cannot express.
 */
export function RichTextToolbarButton({
    label,
    collapse = false,
    format,
    selected,
    className,
    children,
    onClick,
    onMouseDown,
    ...props
}: RichTextToolbarButtonProps): ReactElement {
    const { apply, isActive } = useRichTextFormatting();
    const isSelected = format ? isActive(format) : selected;

    return (
        <ToolbarIconButton
            label={label}
            aria-pressed={isSelected}
            variant={isSelected ? 'primary' : 'secondary'}
            className={cn(collapse && 'hidden @sm:inline-flex', className)}
            // Preventing focus keeps the caret in the editor — without this the phone
            // keyboard drops on every press. Popups still open: they act on pointerdown.
            // Chained, not spread, so a caller's handler cannot replace it.
            onMouseDown={(event) => {
                event.preventDefault();
                onMouseDown?.(event);
            }}
            onClick={(event) => {
                if (format) {
                    apply(format);
                }
                onClick?.(event);
            }}
            {...props}
        >
            {children}
        </ToolbarIconButton>
    );
}

export type RichTextPluginProps = {
    /** Accessible name and tooltip; this package owns no copy, so callers translate. */
    label: string;
    /** Fold into the ⋮ menu on a narrow editor. */
    collapse?: boolean;
};

// Either a format or a custom action, never both — both at once would fire twice. A
// custom action can still name what it lights up for, since a link has no command but
// does have an on-state.
type ToolbarControlProps = RichTextPluginProps & {
    itemKey: string;
    icon: ReactNode;
} & (
        | { format: RichTextFormat; onSelect?: never; active?: never }
        | { format?: never; onSelect: () => void; active?: RichTextActive }
    );

/**
 * Every plugin below is this: register a ⋮ row, render a tooltipped button. The row
 * needs its own handler even for a format, since nothing else applies one for a row.
 */
function ToolbarControl({
    itemKey,
    format,
    active,
    onSelect,
    icon,
    label,
    collapse = false,
}: ToolbarControlProps): ReactElement {
    const { apply, isActive } = useRichTextFormatting();

    useRichTextToolbarItem(
        itemKey,
        {
            label,
            icon,
            format: format ?? active,
            // No `format` guard: with `onSelect` absent, the union narrows to a format.
            onSelect:
                onSelect ??
                (() => {
                    apply(format);
                }),
        },
        collapse,
    );

    return (
        <Tooltip>
            <TooltipTrigger
                render={
                    <RichTextToolbarButton
                        label={label}
                        format={format}
                        selected={active ? isActive(active) : undefined}
                        collapse={collapse}
                        onClick={onSelect}
                    >
                        {icon}
                    </RichTextToolbarButton>
                }
            />
            <TooltipContent>{label}</TooltipContent>
        </Tooltip>
    );
}

export function RichTextBold(props: RichTextPluginProps): ReactElement {
    return <ToolbarControl itemKey="bold" format="bold" icon={<BoldIcon />} {...props} />;
}

export function RichTextItalic(props: RichTextPluginProps): ReactElement {
    return <ToolbarControl itemKey="italic" format="italic" icon={<ItalicIcon />} {...props} />;
}

export function RichTextUnderline(props: RichTextPluginProps): ReactElement {
    return <ToolbarControl itemKey="underline" format="underline" icon={<UnderlineIcon />} {...props} />;
}

export function RichTextStrikethrough(props: RichTextPluginProps): ReactElement {
    return (
        <ToolbarControl
            itemKey="strikethrough"
            format="strikethrough"
            icon={<StrikethroughIcon />}
            {...props}
        />
    );
}

export function RichTextOrderedList(props: RichTextPluginProps): ReactElement {
    return (
        <ToolbarControl itemKey="ordered-list" format="ordered-list" icon={<ListOrderedIcon />} {...props} />
    );
}

export function RichTextBulletList(props: RichTextPluginProps): ReactElement {
    return <ToolbarControl itemKey="bullet-list" format="bullet-list" icon={<ListIcon />} {...props} />;
}

export type RichTextLinkProps = RichTextPluginProps & {
    /** Asks the user for the URL. A `window.prompt` message today, a dialog title later. */
    urlPrompt: string;
};

/**
 * Wraps the selection in a link. Not a `RichTextFormat` — it has to ask for a URL first
 * — so it applies the link itself, but it still lights up inside one.
 */
export function RichTextLink({ label, urlPrompt, collapse = false }: RichTextLinkProps): ReactElement {
    const [editor] = useLexicalComposerContext();

    const insertLink = useCallback(() => {
        const url = prompt(urlPrompt);
        if (!url) {
            return;
        }
        editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) {
                return;
            }
            // With no text selected there is nothing to wrap, and `$toggleLink` would
            // either do nothing at all or swallow the whole text node it sits in. Insert
            // the URL as its own link instead, which is what the caret asks for.
            if (selection.isCollapsed()) {
                const link = $createLinkNode(url, { target: '_blank', rel: 'noreferrer' });
                link.append($createTextNode(url));
                selection.insertNodes([link]);
                return;
            }
            $toggleLink(url, { target: '_blank' });
        });
    }, [editor, urlPrompt]);

    return (
        <ToolbarControl
            itemKey="link"
            icon={<LinkIcon />}
            onSelect={insertLink}
            active="link"
            label={label}
            collapse={collapse}
        />
    );
}

export type RichTextToolbarDividerProps = ComponentPropsWithRef<'div'> & {
    collapse?: boolean;
};

export function RichTextToolbarDivider({
    collapse = false,
    className,
    ...props
}: RichTextToolbarDividerProps): ReactElement {
    return (
        <div
            role="separator"
            aria-orientation="vertical"
            className={cn(
                'mx-tight h-4 w-px shrink-0 bg-line-weak',
                collapse ? 'hidden @sm:block' : 'block',
                className,
            )}
            {...props}
        />
    );
}

export type RichTextToolbarOverflowProps = {
    /** Names the ⋮ button; this package owns no copy, so callers translate. */
    label: string;
};

/**
 * The ⋮ menu, holding a row for every control that collapsed. Shows exactly when the
 * `collapse` class hides, and renders nothing when no control registered.
 *
 * Put it last among the controls. Its rows come from the registry, so it takes no
 * children and there is no second list to keep in step.
 */
export function RichTextToolbarOverflow({ label }: RichTextToolbarOverflowProps): ReactElement | null {
    const { items, barRef } = useRichTextToolbarContext('RichTextToolbarOverflow');
    const { isActive } = useRichTextFormatting();
    const [editor] = useLexicalComposerContext();
    const selectionOnOpen = useRef<BaseSelection | null>(null);

    if (items.size === 0) {
        return null;
    }

    return (
        // `contents` so the divider and the trigger sit in the bar's own flex row.
        <div className="contents @sm:hidden">
            <RichTextToolbarDivider />
            <MenuPrimitive.Root
                // Opening the popup takes focus, and WebKit and Firefox collapse the
                // editor's selection to a caret when it loses focus. A text format then
                // applies to nothing. Keep the range from before the popup opened.
                onOpenChange={(open) => {
                    if (open) {
                        editor.getEditorState().read(() => {
                            selectionOnOpen.current = $getSelection() ?? null;
                        });
                    }
                }}
            >
                <MenuPrimitive.Trigger
                    render={
                        <RichTextToolbarButton label={label}>
                            <EllipsisVerticalIcon />
                        </RichTextToolbarButton>
                    }
                />
                <MenuPrimitive.Portal>
                    <MenuPrimitive.Positioner
                        className="isolate z-(--z-tooltip)"
                        // Anchored to the bar, so the popup lines up with the field's
                        // edge rather than with the ⋮ button.
                        anchor={barRef}
                        side="bottom"
                        align="start"
                        sideOffset={4}
                    >
                        <MenuPrimitive.Popup
                            render={
                                // `outline-none`: the popup takes focus on open, so the
                                // browser paints its own ring around the whole list.
                                // The highlighted row is the focus indicator here.
                                <Menu className="w-48 rounded-default border border-line-weak bg-container shadow-lg outline-none" />
                            }
                        >
                            {[...items].map(([key, item]) => (
                                <MenuPrimitive.Item
                                    key={key}
                                    onClick={() => {
                                        const selection = selectionOnOpen.current;
                                        // Queued before the row's own command, so that
                                        // command reads the restored range. Cloned
                                        // because `$setSelection` writes to what it is
                                        // given and a committed selection is frozen —
                                        // it would throw into the editor's `onError`,
                                        // which callers usually leave silent.
                                        editor.update(() => {
                                            if (selection) {
                                                $setSelection(selection.clone());
                                            }
                                        });
                                        // Read here, so the row runs the newest closure.
                                        item.onSelect.current();
                                    }}
                                    render={
                                        <MenuItem
                                            label={item.label}
                                            className={
                                                item.format && isActive(item.format)
                                                    ? 'text-brand'
                                                    : undefined
                                            }
                                        >
                                            <MenuItemLeading className="*:[svg:not([class*='size-'])]:size-4">
                                                {item.icon}
                                            </MenuItemLeading>
                                            <MenuItemLabel />
                                        </MenuItem>
                                    }
                                />
                            ))}
                        </MenuPrimitive.Popup>
                    </MenuPrimitive.Positioner>
                </MenuPrimitive.Portal>
            </MenuPrimitive.Root>
        </div>
    );
}

export type RichTextToolbarSpacerProps = ComponentPropsWithRef<'div'>;

/** Pushes what follows to the trailing edge of the bar (voice, AI, …). */
export function RichTextToolbarSpacer({ className, ...props }: RichTextToolbarSpacerProps): ReactElement {
    return <div className={cn('flex-1', className)} {...props} />;
}
